import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { PluginContext } from 'ts-components/Plugins/PluginContext';
import Intercom from '@intercom/messenger-js-sdk';
import { Userpilot } from 'userpilot';
import { useLocation } from 'react-router-dom';

const sentryDSN = import.meta.env.VITE_SENTRY_DSN;
const sentryTraceSample = import.meta.env.VITE_SENTRY_TRACESAMPLE;
const sentryENV = import.meta.env.VITE_SENTRY_ENV;
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE;
const intercomAppIdEnv = import.meta.env.VITE_INTERCOM_APP_ID;
const userpilotAppIdEnv = import.meta.env.VITE_USERPILOT_APP_ID;

const isProduction = import.meta.env.MODE === 'production';
const canEnableSentry = isProduction;

// Users with these emails will be ignored and tracking will not be instrumented. This should not affect monitoring
// because that is useful for all users.
const emailExclusions = ['@kizen-e2e.testinator.com', '+e2e@kizen.com'];

if (canEnableSentry) {
  const config = {
    dsn: sentryDSN,
    tracesSampleRate: sentryTraceSample,
    environment: sentryENV,
    release: sentryRelease,
    enableAutoSessionTracking: false,
    autoSessionTracking: false,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  };

  Sentry.init(config);
}

export const InstrumentationProvider = () => {
  const [hasInitializedUserpilot, setHasInitializedUserpilot] = useState(false);
  const teamMember = useSelector((s) => s.authentication.teamMember);
  const business = useSelector((s) => s.authentication.chosenBusiness);
  const { isLoading: isLoadingPlugins, plugins } = useContext(PluginContext);
  const location = useLocation();
  const { pathname } = location;

  // Instrumenting user tracking requires user auth information as well as some plugin
  // information, and should not be done in testing environments.
  const canInstrumentUserTracking = teamMember && business && !isLoadingPlugins;

  // Instrumenting monitoring only requires auth information and can be done without
  // waiting for other checks, and should run in testing environments.
  const canInstrumentMonitoring = teamMember && business;

  useEffect(() => {
    if (canEnableSentry && canInstrumentMonitoring) {
      const { email, user: id, full_name: username } = teamMember;
      Sentry.setUser({
        email,
        id,
        username,
        ip_address: '{{auto}}',
        business_name: business.name,
        business_id: business.id,
      });
      Sentry.setTag('kzn-business', business.name);
    }
  }, [teamMember, business, canInstrumentMonitoring]);

  // If the business has a custom override plugin installed, we need to prefer that over our default one
  const intercomAppId =
    plugins?.intercom?.business_config?.appId || intercomAppIdEnv;
  const userpilotAppId =
    plugins?.userpilot?.business_config?.appId || userpilotAppIdEnv;

  const canEnableUserpilot = userpilotAppId && hasInitializedUserpilot;

  // Only initialize userpilot once. We'd do this outside the render if we didn't
  // have to wait for the plugins to load to get the appId
  if (userpilotAppId && !hasInitializedUserpilot) {
    Userpilot.initialize(userpilotAppId);
    setHasInitializedUserpilot(true);
  }

  useEffect(() => {
    if (canInstrumentUserTracking) {
      const {
        email,
        user: userId,
        full_name: username,
        id: teamMemberId,
        created: teamMemberCreated,
      } = teamMember;

      const {
        id: businessId,
        company_name,
        name: businessName,
        is_trial,
        created: businessCreated,
      } = business;

      const isIgnoredEmail = emailExclusions.some((exclusion) =>
        email.endsWith(exclusion)
      );

      if (isIgnoredEmail) {
        return;
      }

      if (intercomAppId) {
        Intercom({
          app_id: intercomAppId,
          user_id: userId,
          name: username,
          email: email,
          hide_default_launcher: true,
          company: {
            id: businessId,
            name: `${company_name} - ${businessName}`,
          },
        });
      }

      if (canEnableUserpilot) {
        Userpilot.identify(teamMemberId, {
          name: username,
          email: email,
          created_at: teamMemberCreated,
          company: {
            id: businessId,
            name: `${company_name} - ${businessName}`,
            paying: !is_trial,
            created_at: businessCreated,
          },
        });

        // Show the intercom launcher when a new message comes in.
        // If the launcher remains hidden, the new message still show up, just without the lancher.
        // (keegandonley): Disabling this for now because it conflicts with floating frames
        // onUnreadCountChange(() => {
        //   update({ hide_default_launcher: false });
        // });
      }
    }
  }, [
    teamMember,
    business,
    isLoadingPlugins,
    canInstrumentUserTracking,
    intercomAppId,
    canEnableUserpilot,
  ]);

  // Userpilot needs to be reloaded on every route change
  useEffect(() => {
    if (canInstrumentUserTracking) {
      if (canEnableUserpilot) {
        Userpilot.reload();
      }
    }
  }, [pathname, canInstrumentUserTracking, canEnableUserpilot]);

  return null;
};
